import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import Entypo from '@react-native-vector-icons/entypo/static';
import { axiosinstance } from '../../axiosClient';
import { useResetPasscode } from '../../Context/ResetPasscodeContext';
import Typography from '../../components/Reusables/Typography';
import PasswordStrengthIndicator from '../../components/Reusables/PasswordStrengthIndicator';
import { shouldUseWebLayout } from '../../utils/layoutUtils';
import { getApiMessage } from '../../utils/apiError';
import { isPasswordValid } from '../../utils/passwordValidation';
import { borderWidth, fs, s, v } from '../../utils/libs';
import { GTFonts, theme } from '../../theme';
import { isTablet } from 'react-native-device-info';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';

const ResetPasscodeNewPasscodeScreen = ({
  navigation,
}: {
  navigation: any;
}) => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isLargeScreen = windowWidth >= 768;
  const useWebLikeLayout = shouldUseWebLayout(windowWidth, windowHeight);
  const { session, clearSession } = useResetPasscode();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [passwordBlurred, setPasswordBlurred] = useState(false);

  const validPassword = isPasswordValid(password);
  const validMatch = password === confirmPassword && confirmPassword.length > 0;

  useEffect(() => {
    if (!session?.ownerUserId || !session?.tempAccessToken) {
      setErrMsg('Session expired. Returning to login…');
      const t = setTimeout(() => {
        clearSession();
        navigation.navigate('Login');
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [
    session?.ownerUserId,
    session?.tempAccessToken,
    clearSession,
    navigation,
  ]);

  const handleSubmit = async () => {
    setErrMsg('');
    if (!session?.tempAccessToken) return;

    if (!isPasswordValid(password)) {
      setPasswordBlurred(true);
      setErrMsg('Please fix the password issues highlighted below.');
      return;
    }
    if (!validMatch) {
      setErrMsg('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      console.log('bp_owner_set_new_password_information', 'payload', {
        bp_new_password: '[redacted]',
      });
      const response = await axiosinstance.post(
        'bp_owner_set_new_password_information',
        JSON.stringify({
          bp_new_password: password,
        }),
        {
          headers: {
            Authorization: `Bearer ${session.tempAccessToken}`,
          },
        },
      );
      console.log(
        'bp_owner_set_new_password_information',
        response?.data ?? response,
      );

      if (response?.data?.status === 200) {
        appAnalytics.track('auth_reset_passcode_completed', {
          ...buildAppAnalyticsContext(),
        });
        clearSession();
        navigation.navigate('Login');
        return;
      }
      setErrMsg(
        typeof response?.data?.message === 'string'
          ? response.data.message
          : 'Unable to update password',
      );
    } catch (e: any) {
      console.log(
        'bp_owner_set_new_password_information error',
        e?.response?.status,
        e?.response?.data ?? e,
      );
      const status = e?.response?.status;
      const backendMsg = e?.response?.data?.message;
      if (status === 403 && typeof backendMsg === 'string' && backendMsg) {
        setErrMsg(backendMsg);
        return;
      }
      setErrMsg(getApiMessage(e, 'Unable to update password'));
    } finally {
      setLoading(false);
    }
  };

  const goBackToLogin = useCallback(() => {
    clearSession();
    navigation.navigate('Login');
  }, [clearSession, navigation]);

  const passwordFields = (
    <>
      {errMsg ? (
        <View style={styles.errorContainer}>
          <Typography style={styles.errorText}>{errMsg}</Typography>
        </View>
      ) : null}
      <Typography style={styles.label}>New password</Typography>
      <View style={styles.inputRow}>
        <TextInput
          style={styles.input}
          secureTextEntry={!showPassword}
          autoCorrect={false}
          placeholder="New password"
          placeholderTextColor={theme.colors.textTertiary}
          value={password}
          onChangeText={setPassword}
          onBlur={() => {
            if (password.length > 0) setPasswordBlurred(true);
          }}
        />
        <TouchableOpacity
          style={styles.eye}
          onPress={() => setShowPassword(p => !p)}
          hitSlop={12}>
          <Entypo
            name={showPassword ? 'eye' : 'eye-with-line'}
            size={22}
            color={theme.colors.textGray500}
          />
        </TouchableOpacity>
      </View>
      <PasswordStrengthIndicator
        password={password}
        hasBlurred={passwordBlurred}
      />
      <Typography style={styles.label}>Confirm password</Typography>
      <View style={styles.inputRow}>
        <TextInput
          style={styles.input}
          secureTextEntry={!showConfirm}
          autoCorrect={false}
          placeholder="Confirm password"
          placeholderTextColor={theme.colors.textTertiary}
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          returnKeyType="done"
          onSubmitEditing={() => {
            if (loading || !validPassword || !validMatch) return;
            void handleSubmit();
          }}
        />
        <TouchableOpacity
          style={styles.eye}
          onPress={() => setShowConfirm(p => !p)}
          hitSlop={12}>
          <Entypo
            name={showConfirm ? 'eye' : 'eye-with-line'}
            size={22}
            color={theme.colors.textGray500}
          />
        </TouchableOpacity>
      </View>
      {confirmPassword.length > 0 && !validMatch ? (
        <Typography style={styles.mismatch}>Passwords do not match</Typography>
      ) : null}
      <TouchableOpacity
        style={[
          styles.primaryButton,
          (!validPassword || !validMatch || loading) &&
            styles.primaryButtonDisabled,
        ]}
        disabled={!validPassword || !validMatch || loading}
        onPress={() => void handleSubmit()}>
        {loading ? (
          <ActivityIndicator color={theme.colors.textInverse} />
        ) : (
          <Typography style={styles.primaryButtonText}>
            Update passcode
          </Typography>
        )}
      </TouchableOpacity>
      <TouchableOpacity onPress={goBackToLogin} style={styles.linkRow}>
        <Typography style={styles.linkText}>Back to login</Typography>
      </TouchableOpacity>
    </>
  );

  if (useWebLikeLayout && isLargeScreen) {
    return (
      <View style={styles.webOuter}>
        <ScrollView
          contentContainerStyle={styles.webScrollContent}
          keyboardShouldPersistTaps="handled">
          <View style={styles.webFormCard}>
            <Typography style={styles.title}>Create new passcode</Typography>
            {passwordFields}
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Typography style={styles.title}>Create new passcode</Typography>
      {passwordFields}
    </View>
  );
};

export default ResetPasscodeNewPasscodeScreen;

const styles = StyleSheet.create({
  webOuter: {
    flex: 1,
    backgroundColor: theme.colors.backgroundGray50,
    minHeight: '100%',
  },
  webScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: s(24),
    paddingVertical: v(48),
  },
  webFormCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: s(24),
    padding: s(40),
    width: '100%',
    maxWidth: s(480),
    borderWidth: borderWidth(1),
    borderColor: theme.colors.borderGray200,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
    padding: s(24),
    paddingTop: v(24),
  },
  title: {
    fontSize: fs(22),
    lineHeight: fs(26),
    fontFamily: GTFonts.semibold,
    color: theme.colors.primary,
    marginBottom: v(12),
  },
  label: {
    fontSize: fs(14),
    lineHeight: fs(20),
    fontFamily: GTFonts.semibold,
    color: theme.colors.textGray700,
    marginBottom: v(6),
    marginTop: v(4),
  },
  errorContainer: {
    backgroundColor: theme.colors.errorLight,
    borderRadius: s(8),
    padding: s(12),
    marginBottom: v(12),
  },
  errorText: {
    color: theme.colors.errorDark,
    fontSize: fs(14),
    lineHeight: fs(20),
  },
  mismatch: {
    color: theme.colors.errorDark,
    fontSize: fs(13),
    marginTop: v(4),
    marginBottom: v(8),
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
    borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
    borderColor: theme.colors.borderGray200,
    borderRadius: s(8),
    marginBottom: v(8),
  },
  input: {
    flex: 1,
    paddingHorizontal: s(14),
    paddingVertical: v(12),
    fontSize: fs(16),
    lineHeight: fs(20),
    color: theme.colors.textGray900,
    fontFamily: GTFonts.regular,
  },
  eye: {
    paddingHorizontal: s(12),
    paddingVertical: v(8),
  },
  primaryButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: s(8),
    paddingVertical: v(14),
    alignItems: 'center',
    marginTop: v(16),
  },
  primaryButtonDisabled: {
    backgroundColor: theme.colors.primaryLight,
  },
  primaryButtonText: {
    color: theme.colors.textInverse,
    fontSize: isTablet() ? 18 : 16,
    lineHeight: fs(isTablet() ? 22 : 20),
    fontWeight: '600',
    fontFamily: GTFonts.semibold,
  },
  linkRow: {
    alignItems: 'center',
    marginTop: v(20),
  },
  linkText: {
    color: theme.colors.primary,
    fontSize: fs(14),
    lineHeight: fs(16),
    fontFamily: GTFonts.semibold,
    textDecorationLine: 'underline',
  },
});
